<template>
  <section id="download" class="download">
    <div class="container">
      <div class="section-header">
        <div class="section-badge">
          <span class="badge-icon">📱</span>
          <span>立即下载</span>
        </div>
        <h2 class="section-title">连接全产业链</h2>
        <p class="section-subtitle">下载企业通讯录安卓版，开启智能商务沟通新时代</p>
      </div>
      
      <div class="download-content">
        <div class="download-info">
          <div class="download-stats">
            <div class="stat-item" v-for="(stat, index) in stats" :key="index">
              <div class="stat-number">{{ stat.number }}</div>
              <div class="stat-label">{{ stat.label }}</div>
            </div>
          </div>
          
          <div class="download-features">
            <div class="feature-item" v-for="(feature, index) in features" :key="index">
              <div class="feature-icon">{{ feature.icon }}</div>
              <span>{{ feature.text }}</span>
            </div>
          </div>
        </div>
        
        <div class="download-actions">
          <div class="download-buttons">
            <!-- 加载中状态 -->
            <div v-if="loading" class="loading-message">
              <div class="loading-spinner"></div>
              <p>正在加载版本信息...</p>
            </div>

            <!-- 错误状态 -->
            <div v-else-if="error" class="error-message">
              <p>{{ error }}</p>
              <button @click="fetchVersions" class="btn-retry">重试</button>
            </div>

            <!-- 无数据状态 -->
            <div v-else-if="versions.length === 0" class="no-versions">
              <p>暂无可用版本</p>
            </div>

            <!-- 版本列表 -->
            <div v-else class="versions-list">
              <div
                v-for="version in versions"
                :key="version.id"
                class="version-item"
                :class="{ 'is-active': version.is_active === 1 }"
              >
                <div class="version-header">
                  <div class="version-title">
                    <span class="version-icon">📦</span>
                    <span class="version-number">版本 {{ version.version }}</span>
                    <span v-if="version.is_active === 1" class="active-badge">当前版本</span>
                    <span v-if="version.force_update" class="force-badge">强制更新</span>
                  </div>
                  <button
                    class="download-btn-small"
                    @click="handleDownload(version.download_url)"
                  >
                    <span class="download-icon">⬇</span>
                    下载
                  </button>
                </div>

                <div class="version-description">
                  {{ version.description }}
                </div>

                <div class="version-footer">
                  <span class="update-time">
                    更新时间: {{ formatDate(version.updated_at) }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          
          <div class="download-requirements">
            <h3>系统要求</h3>
            <div class="requirements-list">
              <div class="requirement-item">
                <span class="requirement-icon">📱</span>
                <span>Android 6.0 及以上</span>
              </div>
              <div class="requirement-item">
                <span class="requirement-icon">💾</span>
                <span>存储空间 50MB</span>
              </div>
              <div class="requirement-item">
                <span class="requirement-icon">🌐</span>
                <span>网络连接</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'DownloadSection',
  data() {
    return {
      stats: [
        { number: '50万+', label: '注册用户' },
        { number: '10万+', label: '企业资源' },
        { number: '99%', label: '用户好评' }
      ],
      features: [
        { icon: '⚡', text: '快速查找' },
        { icon: '🔗', text: '产业链对接' },
        { icon: '🛡️', text: '数据安全' }
      ],
      versions: [],
      loading: false,
      error: null
    }
  },
  mounted() {
    this.fetchVersions()
  },
  methods: {
    async fetchVersions() {
      this.loading = true
      this.error = null
      try {
        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '/api'
        const response = await fetch(`${apiBaseUrl}/app-versions/list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            page_num: 1,
            page_size: 50,
            version: ''
          })
        })

        if (!response.ok) {
          throw new Error(`请求失败: ${response.status}`)
        }

        const data = await response.json()
        console.log('版本数据:', data)

        if (data && data.data) {
          let resp = data.data.items || data.data.list || data.data || []
          if (Array.isArray(resp)) {
            this.versions = resp
          }
        }
      } catch (error) {
        console.error('获取版本失败:', error)
        this.error = error.message || '加载失败,请稍后重试'
      } finally {
        this.loading = false
      }
    },
    handleDownload(url) {
      if (url) {
        window.open(url, '_blank')
      } else {
        alert('下载链接无效')
      }
    },
    formatDate(dateString) {
      if (!dateString || dateString === '0001-01-01T00:00:00Z') {
        return '未知'
      }
      const date = new Date(dateString)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.download {
  padding: 100px 0;
  background: linear-gradient(135deg, #0a0e17 0%, #1a1f2e 50%, #0a0e17 100%);
  position: relative;
  overflow: hidden;
}

.download::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: 
    radial-gradient(circle at 20% 80%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(0, 153, 204, 0.1) 0%, transparent 50%);
  pointer-events: none;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
}

.section-header {
  text-align: center;
  margin-bottom: 80px;
}

.section-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 20px;
  padding: 8px 16px;
  font-size: 0.9rem;
  color: #00d4ff;
  margin-bottom: 24px;
  backdrop-filter: blur(10px);
}

.badge-icon {
  font-size: 1rem;
}

.section-title {
  font-size: 3rem;
  font-weight: 800;
  color: #ffffff;
  margin-bottom: 16px;
  background: linear-gradient(45deg, #00d4ff, #ffffff);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #8892b0;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
}

.download-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;
}

.download-info {
  color: #ffffff;
}

.download-stats {
  display: flex;
  gap: 40px;
  margin-bottom: 60px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 2.5rem;
  font-weight: 800;
  color: #00d4ff;
  margin-bottom: 8px;
}

.stat-label {
  font-size: 1rem;
  color: #8892b0;
}

.download-features {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 1.1rem;
  color: #a0aec0;
}

.feature-icon {
  font-size: 1.5rem;
  width: 40px;
  text-align: center;
}

.download-actions {
  display: flex;
  flex-direction: column;
  gap: 40px;
}

.download-buttons {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.download-btn {
  display: flex;
  align-items: center;
  gap: 20px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px 32px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

.download-btn:hover {
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1), rgba(0, 153, 204, 0.1));
  border-color: rgba(0, 212, 255, 0.3);
  transform: translateY(-4px);
  box-shadow: 0 20px 40px rgba(0, 212, 255, 0.1);
}

.btn-icon {
  font-size: 2rem;
  width: 60px;
  text-align: center;
}

.btn-content {
  flex: 1;
}

.btn-label {
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 4px;
}

.btn-version {
  font-size: 0.9rem;
  color: #8892b0;
}

.btn-glow {
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s ease;
}

.download-btn:hover .btn-glow {
  left: 100%;
}

.loading-message,
.error-message,
.no-versions {
  text-align: center;
  padding: 40px;
  color: #8892b0;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(0, 212, 255, 0.3);
  border-radius: 50%;
  border-top-color: #00d4ff;
  animation: spin 1s ease-in-out infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-message {
  color: #ff6b6b;
}

.btn-retry {
  margin-top: 16px;
  padding: 10px 24px;
  background: linear-gradient(45deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-retry:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 212, 255, 0.3);
}

.versions-list {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.version-item {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 24px;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.version-item:hover {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(0, 212, 255, 0.3);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 212, 255, 0.1);
}

.version-item.is-active {
  border-color: rgba(0, 212, 255, 0.5);
  background: rgba(0, 212, 255, 0.08);
}

.version-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 16px;
}

.version-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.version-icon {
  font-size: 1.5rem;
}

.version-number {
  font-size: 1.2rem;
  font-weight: 700;
  color: #ffffff;
}

.active-badge {
  display: inline-block;
  background: linear-gradient(45deg, #00d4ff, #0099cc);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
}

.force-badge {
  display: inline-block;
  background: linear-gradient(45deg, #ff6b6b, #ff8e53);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
}

.download-btn-small {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  background: linear-gradient(45deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.download-btn-small:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 212, 255, 0.4);
}

.download-icon {
  font-size: 1.1rem;
}

.version-description {
  color: #a0aec0;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 12px;
  white-space: pre-line;
}

.version-footer {
  display: flex;
  justify-content: flex-end;
}

.update-time {
  color: #8892b0;
  font-size: 0.85rem;
}

.download-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.download-btn:disabled:hover {
  transform: none;
  box-shadow: none;
}

.version-info {
  background: rgba(0, 212, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.2);
  border-radius: 12px;
  padding: 20px;
  margin-top: 20px;
}

.info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  color: #00d4ff;
  font-weight: 600;
}

.info-icon {
  font-size: 1.2rem;
}

.info-title {
  font-size: 1rem;
}

.version-description {
  color: #a0aec0;
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 12px;
  white-space: pre-line;
}

.version-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.force-update-badge {
  display: inline-block;
  background: linear-gradient(45deg, #ff6b6b, #ff8e53);
  color: #ffffff;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 12px;
}

.update-time {
  color: #8892b0;
  font-size: 0.85rem;
}

.download-requirements {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 30px;
  backdrop-filter: blur(10px);
}

.download-requirements h3 {
  color: #ffffff;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 20px;
}

.requirements-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.requirement-item {
  display: flex;
  align-items: center;
  gap: 12px;
  color: #a0aec0;
  font-size: 1rem;
}

.requirement-icon {
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
}

@media (max-width: 768px) {
  .download {
    padding: 60px 0;
  }
  
  .section-title {
    font-size: 2.5rem;
  }
  
  .download-content {
    grid-template-columns: 1fr;
    gap: 40px;
    text-align: center;
  }
  
  .download-stats {
    justify-content: center;
    gap: 30px;
  }
  
  .stat-number {
    font-size: 2rem;
  }
  
  .download-features {
    align-items: center;
  }
  
  .feature-item {
    justify-content: center;
  }
  
  .download-btn {
    padding: 20px 24px;
  }
  
  .btn-icon {
    font-size: 1.5rem;
    width: 40px;
  }
  
  .btn-label {
    font-size: 1rem;
  }
}
</style>
